import { Shield, Heart, Users, CheckCircle, AlertCircle } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const SpecialNeedsTrust = () => {
  const mainContent = (
    <>
      {/* Overview Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">Special Needs Trust Planning</h2>
            <p className="text-lg text-muted-foreground mb-8">
              A Special Needs Trust (SNT) allows you to provide financial support for a loved one with disabilities without jeopardizing their eligibility for government benefits like SSI, Medicaid, or other assistance programs.
            </p>

            <div className="bg-accent/10 border-l-4 border-accent p-6 rounded-r-lg mb-12">
              <p className="text-lg font-semibold mb-2">
                <AlertCircle className="inline h-5 w-5 mr-2 text-accent" />
                Why Special Needs Trusts Matter
              </p>
              <p className="text-muted-foreground">
                Government benefit programs have strict asset and income limits. A properly structured Special Needs Trust preserves eligibility while ensuring your loved one can enjoy a higher quality of life.
              </p>
            </div>

            <h3 className="text-3xl font-bold mb-8">Types of Special Needs Trusts</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Shield className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Third-Party Special Needs Trust</h3>
                <p className="text-muted-foreground mb-4">
                  Created and funded by someone other than the beneficiary (typically parents or grandparents).
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>No payback requirement to state Medicaid</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Can be included in parent's estate plan</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Greater flexibility in distribution</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Beneficiary maintains government benefits</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Users className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">First-Party (Self-Settled) Trust</h3>
                <p className="text-muted-foreground mb-4">
                  Funded with the beneficiary's own assets (e.g., from inheritance, settlement, or back-pay).
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Beneficiary must be under age 65 when established</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Must include Medicaid payback provision</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Protects settlement funds and inheritances</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Preserves eligibility for benefits</span>
                  </li>
                </ul>
              </div>
            </div>

            <h3 className="text-3xl font-bold mb-6">What Can Trust Funds Be Used For?</h3>
            <div className="bg-card p-8 rounded-lg border border-accent/20 mb-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-lg mb-4 text-accent">Allowed Expenses</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Education and tutoring</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Recreation and entertainment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Medical and dental care not covered by insurance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Therapy and rehabilitation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Computer and electronic equipment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Vehicle and transportation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Travel and vacations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Personal care attendant</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-4 text-red-500">Expenses to Avoid</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>Food (if receiving food stamps)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>Shelter/housing (may reduce SSI)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>Direct cash to beneficiary</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>Utilities (may reduce SSI)</span>
                    </li>
                  </ul>
                  <p className="mt-4 text-sm text-muted-foreground">
                    * These expenses may reduce or eliminate government benefits. Careful planning is essential.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABLE Accounts */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">ABLE Accounts: A Complementary Tool</h2>
            <p className="text-lg text-muted-foreground mb-8">
              ABLE (Achieving a Better Life Experience) accounts provide an additional option for individuals with disabilities who became disabled before age 26.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">ABLE Account Benefits</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Simpler to manage than trusts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Tax-advantaged savings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Beneficiary can control account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Annual contribution limit ($18,000)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">ABLE Account Limitations</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>$100,000 balance limit for SSI eligibility</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>Must be disabled before age 26</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>Medicaid payback upon death</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>Annual contribution limits</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 bg-accent/10 p-6 rounded-lg">
              <p className="text-center font-semibold">
                Many families use both an ABLE account AND a Special Needs Trust for comprehensive planning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">Why Choose Us for Special Needs Planning?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <Heart className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Compassionate Expertise</h3>
                <p className="text-muted-foreground">We understand the unique challenges families face and provide sensitive guidance</p>
              </div>
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <Shield className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Benefits Protection</h3>
                <p className="text-muted-foreground">Ensure government benefits remain intact while providing for quality of life</p>
              </div>
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <Users className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Long-Term Planning</h3>
                <p className="text-muted-foreground">Create a sustainable plan that provides for your loved one's lifetime needs</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );

  return (
    <GenericPracticePage
      title="Special Needs Trust"
      subtitle="Protecting Government Benefits While Providing for Your Loved One's Future"
      icon={<Shield className="h-16 w-16" />}
      mainContent={mainContent}
    />
  );
};

export default SpecialNeedsTrust;